import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { CustomSelect, btnPrimary, btnSecondary } from '@redpanda/ui';
import { formatTableDate, isAdmin, useRefetchOnFocus } from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import { useTimeEntries } from '../stores/timeEntryStore';
import { useMembers } from '../stores/lookupStore';
import { useDataChanged } from '../utils/dataEvents';
import TimeEntryTable, { formatHours } from '../components/TimeEntryTable';
import TimeEntryDialog from '../components/TimeEntryDialog';
import MemberRatesDialog from '../components/MemberRatesDialog';

/** 本地日期 YYYY-MM-DD（不用 toISOString，避免時區把日期往前推一天） */
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** 某天所在那一週的週一 */
const mondayOf = (d: Date) => {
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  day.setDate(day.getDate() - ((day.getDay() + 6) % 7));
  return day;
};
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/**
 * 工時：一週一頁，預設看自己的記錄；管理員可以看全部或指定成員，並設定成員成本費率
 * 每日小計列在表格上方，一眼看出哪天沒記
 */
const TimesheetPage: React.FC = () => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const { user } = useContext(UserContext)!;
  const admin = isAdmin(user?.role);
  const members = useMembers();
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [memberFilter, setMemberFilter] = useState(''); // ''＝我、'all'＝全部成員、其他＝成員 id
  const [adding, setAdding] = useState(false);
  const [editingRates, setEditingRates] = useState(false);

  const query = { from: ymd(weekStart), to: ymd(addDays(weekStart, 6)), userId: memberFilter || undefined };
  const { entries, refetch, key } = useTimeEntries(query);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refetch(); }, [key]);
  useDataChanged('timeEntries', refetch);
  useRefetchOnFocus(refetch);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const hoursOn = (d: Date) => (entries ?? []).filter(e => e.date.slice(0, 10) === ymd(d)).reduce((sum, e) => sum + e.hours, 0);
  const isThisWeek = ymd(weekStart) === ymd(mondayOf(new Date()));
  const weekday = (d: Date) => d.toLocaleDateString(i18n.language, { weekday: 'short' });

  const memberOptions = [
    { value: '', label: t('pmTime.filter.me', { ns: 'pages' }) },
    { value: 'all', label: t('pmTime.filter.all', { ns: 'pages' }) },
    ...members.filter(m => m.id !== user?.id).map(m => ({ value: m.id, label: m.name || m.email })),
  ];

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('pmTime.title', { ns: 'pages' })}</h1>
          <div className="flex space-x-3">
            {admin && (
              <button type="button" onClick={() => setEditingRates(true)} className={btnSecondary}>
                {t('pmTime.rates.button', { ns: 'pages' })}
              </button>
            )}
            <button type="button" onClick={() => setAdding(true)} className={btnPrimary}>
              {t('pmTime.addButton', { ns: 'pages' })}
            </button>
          </div>
        </div>
      </div>

      {/* 週切換＋成員篩選（與其他頁搜尋列同高 h-10） */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex h-10 items-center rounded-md border border-line bg-surface">
          <button type="button" onClick={() => setWeekStart(w => addDays(w, -7))} className="h-full px-2.5 text-fg-secondary hover:text-fg" aria-label={t('pmTime.prevWeek', { ns: 'pages' })}>
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <span className="px-2 text-sm text-fg tabular-nums">
            {formatTableDate(ymd(weekStart), i18n.language)} – {formatTableDate(ymd(addDays(weekStart, 6)), i18n.language)}
          </span>
          <button type="button" onClick={() => setWeekStart(w => addDays(w, 7))} className="h-full px-2.5 text-fg-secondary hover:text-fg" aria-label={t('pmTime.nextWeek', { ns: 'pages' })}>
            <ChevronRightIcon className="h-4 w-4" />
          </button>
          {!isThisWeek && (
            <button type="button" onClick={() => setWeekStart(mondayOf(new Date()))} className="h-full border-l border-line px-3 text-sm text-accent hover:underline">
              {t('pmTime.thisWeek', { ns: 'pages' })}
            </button>
          )}
        </div>
        {admin && (
          <div className="w-48">
            <CustomSelect value={memberFilter} onChange={setMemberFilter} options={memberOptions} searchable={memberOptions.length > 8} />
          </div>
        )}
      </div>

      {/* 每日小計 */}
      <div className="mb-4 grid grid-cols-7 overflow-hidden rounded-lg border border-line bg-surface divide-x divide-line">
        {days.map(d => {
          const h = hoursOn(d);
          const today = ymd(d) === ymd(new Date());
          return (
            <div key={ymd(d)} className={`px-1 sm:px-3 py-2.5 text-center ${today ? 'bg-accent-soft' : ''}`}>
              {/* 手機只顯示「一 12」，寬螢幕才加月份 */}
              <div className={`text-[13px] whitespace-nowrap ${today ? 'text-accent font-medium' : 'text-fg-secondary'}`}>
                <span className="sm:hidden">{d.toLocaleDateString(i18n.language, { weekday: 'narrow' })} {d.getDate()}</span>
                <span className="hidden sm:inline">{weekday(d)} {d.getMonth() + 1}/{d.getDate()}</span>
              </div>
              <div className={`mt-0.5 text-lg font-semibold tabular-nums ${h > 0 ? 'text-fg' : 'text-fg-muted'}`}>{h > 0 ? formatHours(h) : '—'}</div>
            </div>
          );
        })}
      </div>

      <TimeEntryTable
        entries={entries}
        showMember={!!memberFilter}
        emptyTitle={t('pmTime.emptyWeek', { ns: 'pages' })}
        emptyHint={t('pmTime.emptyHint', { ns: 'pages' })}
      />

      {adding && <TimeEntryDialog isOpen onClose={() => setAdding(false)} />}
      {editingRates && <MemberRatesDialog onClose={() => setEditingRates(false)} />}
    </div>
  );
};

export default TimesheetPage;
